/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { PriceLine, ProductPrice, SalesAgreementPriceLine } from '@msdyn365-commerce/retail-proxy';
import { Collapse, format, ICollapseProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import get from 'lodash/get';

const defaultPrice: number = 0;
const freePrice: number = 0;

/**
 * Interface for price component resources.
 * @param {string} priceRangeSeparator - The price range separator.
 */
export interface IPriceComponentResources {
    priceRangeSeparator?: string;
    salesAgreementPricePrompt?: string;
    salesAgreementExpirationDatePrompt?: string;
    salesAgreementCommittedQuantityPrompt?: string;
    salesAgreementRemainingQuantityPrompt?: string;
}

/**
 * Interface for price component props.
 * @param {boolean} isPriceMinMaxEnabled - Whether the price range feature is enabled or not.
 * @param {IPriceComponentResources} priceResources - The price range resources.
 */
export interface IPriceComponentProps extends IComponentProps<{ price: ProductPrice }> {
    className?: string;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    isPriceMinMaxEnabled?: boolean;
    isSalesAgreementPriceFeatureEnabled?: boolean;
    salesAgreementLineId?: number;
    priceResources?: IPriceComponentResources;
    salesAgreementDetailCollapseProps?: ICollapseProps;
    salesAgreementDetailOnClick?(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void;
}

/**
 * The interface of price component.
 */
export interface IPriceComponent extends IComponent<IPriceComponentProps> { }

const priceComponentActions = {};

/**
 * Get the original price from price component props.
 * @param props - The IPriceComponentProps.
 * @returns The original price.
 */
const getOriginalPrice = (props: IPriceComponentProps): number => {
    return Math.max(
        props.data.price.BasePrice ?? defaultPrice,
        props.data.price.TradeAgreementPrice ?? defaultPrice,
        props.data.price.AdjustedPrice ?? defaultPrice
    );
};

/**
 * Get the formatted currency.
 * @param props - The props.
 * @param price - The price.
 * @param shouldUseFreePriceText - The shouldUseFreePriceText.
 * @returns The formatted currency.
 */
const formatCurrency = (props: IPriceComponentProps, price: number | undefined, shouldUseFreePriceText: boolean | undefined): string => {
    if (price === undefined) {
        return '';
    }

    if (shouldUseFreePriceText && price === freePrice && props.freePriceText) {
        const countryRegionId: string = get(props, 'context.actionContext.requestContext.channel.CountryRegionId') || '';

        return countryRegionId === 'MEX'
            ? props.freePriceText
            : (countryRegionId === 'USA' || countryRegionId === 'AUS')
                && (props.freePriceText === 'Not applicable'
                    || props.freePriceText === 'Free'
                    || props.freePriceText === 'FREE')
                ? 'ZERO'
                : props.freePriceText;
    }

    return props.context.cultureFormatter.formatCurrency(price);
};

/**
 * ShowStrikethroughPricing.
 * @param props - The props.
 * @returns True if showStrikethroughPricing, false otherwise.
 */
const showStrikethroughPricing = (props: IPriceComponentProps): boolean => {
    const originalPrice = getOriginalPrice(props);
    if (
        props.isPriceMinMaxEnabled &&
        props.data.price.MaxVariantPrice &&
        props.data.price.MinVariantPrice &&
        props.data.price.MaxVariantPrice > props.data.price.MinVariantPrice
    ) {
        return props.data.price.MinVariantPrice < originalPrice;
    }

    if (originalPrice && props.data.price.CustomerContextualPrice) {
        return originalPrice > props.data.price.CustomerContextualPrice;
    }

    return false;
};

/**
 * Render current price.
 * @param props - The props.
 * @returns The JSX.Element.
 */
const renderCurrentPrice = (props: IPriceComponentProps): JSX.Element => {
    const maxVariantPrice: string = formatCurrency(props, props.data.price.MaxVariantPrice, false);
    const minVariantPrice: string = formatCurrency(props, props.data.price.MinVariantPrice, false);
    if (
        props.isPriceMinMaxEnabled &&
        props.data.price.MaxVariantPrice &&
        props.data.price.MinVariantPrice &&
        props.data.price.MaxVariantPrice > props.data.price.MinVariantPrice
    ) {
        return (
            <span className='msc-price__pricerange' itemProp='price'>
                <span className='msc-price__minprice' itemProp='price'>
                    {minVariantPrice}
                </span>
                <span className='msc-price__separator' itemProp='price'>
                    {props.priceResources?.priceRangeSeparator}
                </span>
                <span className='msc-price__maxprice' itemProp='price'>
                    {maxVariantPrice}
                </span>
            </span>
        );
    }

    return (
        <span className='msc-price__actual' itemProp='price'>
            {formatCurrency(props, props.data.price.CustomerContextualPrice, true)}
        </span>
    );
};

/**
 * Render current price with original price.
 * @param props - The props.
 * @returns The JSX.Element.
 */
const renderCurrentPriceWithOriginalPrice = (props: IPriceComponentProps): JSX.Element => {
    const originalPrice: number = getOriginalPrice(props);
    const initialPrice: string = formatCurrency(props, originalPrice, true);

    return (
        <>
            <span className='sr-only'>
                {` `}
                {props.originalPriceText}
                {` `}
                {initialPrice}
                {` `}
                {props.currentPriceText}
                {` `}
                {renderCurrentPrice(props)}
            </span>
            <span className='msc-price__strikethrough' aria-hidden='true'>
                {initialPrice}
                {` `}
            </span>
            <span aria-hidden='true'>{renderCurrentPrice(props)}</span>
            {props.savingsText && <span className='msc-price__savings'>{props.savingsText}</span>}
        </>
    );
};

/**
 * The state of sales agreement details.
 */
export interface ISalesAgreementDetailState {
    isOpen: boolean;
}

/**
 * The SalesAgreementPrice class.
 */
// eslint-disable-next-line react/require-optimization -- Not needed.
class SalesAgreementPrice extends React.Component<IPriceComponentProps, ISalesAgreementDetailState> {
    public constructor(props: IPriceComponentProps) {
        super(props);
        this._toggle = this._toggle.bind(this);
        this.state = {
            isOpen: true
        } as ISalesAgreementDetailState;
    }

    public render(): React.ReactElement {
        const priceLines: PriceLine[] = this.props.data.price.AttainablePriceLines ?? [];
        const firstPriceLine: SalesAgreementPriceLine = priceLines[0] as SalesAgreementPriceLine;
        if (this.props.salesAgreementDetailCollapseProps) {
            this.props.salesAgreementDetailCollapseProps.isOpen = this.state.isOpen;
        }
        return (
            <span className={classnames('msc-price__salesagreementprice', this.props.className)}>
                <p className='msc-price__actual' itemProp='price'>
                    {formatCurrency(
                        this.props,
                        this.props.data.price.SalesAgreementPrice ?? this.props.data.price.CustomerContextualPrice,
                        true
                    )}
                </p>
                <p
                    className={this.state.isOpen ? 'msc-price__salesagreementprompt_expanded' : 'msc-price__salesagreementprompt_collapsed'}
                    onClick={this._toggle}
                >
                    {this.props.priceResources?.salesAgreementPricePrompt}
                </p>
                <Collapse {...this.props.salesAgreementDetailCollapseProps}>
                    <p className={classnames('msc-price__salesagreementdetail', this.props.className)}>
                        <span>
                            {/* Available date */}
                            {// eslint-disable-next-line prefer-template -- Copied from other class.
                                ' · ' +
                                format(
                                    this.props.priceResources?.salesAgreementExpirationDatePrompt ?? '',
                                    this.props.context.cultureFormatter.formatDate(firstPriceLine.EffectiveDate as Date),
                                    this.props.context.cultureFormatter.formatDate(firstPriceLine.ExpirationDate as Date)
                                )}
                        </span>
                        <span>
                            {/* Quantity commitment */}
                            {// eslint-disable-next-line prefer-template -- Copied from other class.
                                ' · ' +
                                format(
                                    this.props.priceResources?.salesAgreementCommittedQuantityPrompt ?? '',
                                    firstPriceLine.CommittedQuantity
                                )}
                        </span>
                        <span>
                            {/* Remaining quantity to qualify */}
                            {// eslint-disable-next-line prefer-template -- Copied from other class.
                                ' · ' +
                                format(
                                    this.props.priceResources?.salesAgreementRemainingQuantityPrompt ?? '',
                                    firstPriceLine.RemainingQuantity
                                )}
                        </span>
                    </p>
                </Collapse>
            </span>
        );
    }

    /**
     * Callback which toggles collapse mode.
     * @param event - The mouse or keyboard event.
     */
    private _toggle(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void {
        event.preventDefault();
        console.log(this.state.isOpen);
        // eslint-disable-next-line react/no-access-state-in-setstate -- Copied from other class.
        this.setState({ isOpen: !this.state.isOpen });
        console.log(this.state.isOpen);
    }
}

/**
 * Price.
 * @param props - The props.
 * @returns The JSX.Element.
 */
const Price: React.FC<IPriceComponentProps> = (props: IPriceComponentProps): JSX.Element | null => {
    // CustomerContextualPrice could be zero
    if (props.data.price.CustomerContextualPrice === undefined) {
        return null;
    }

    // Determine if contract price is avaiable/applied by:
    //   isFeatureEnabled and SA price != 0, for PDP.
    //   SA line id != 0, for cart line.
    if (props.isSalesAgreementPriceFeatureEnabled && props.data.price.SalesAgreementPrice !== 0) {
        const salesAgreementPrice = new SalesAgreementPrice(props);
        return <span className={classnames('msc-price', props.className)}>{salesAgreementPrice.render()}</span>;
    }

    return (
        <span className={classnames('msc-price', props.className)}>
            {showStrikethroughPricing(props) ? renderCurrentPriceWithOriginalPrice(props) : renderCurrentPrice(props)}
        </span>
    );
};

/**
 * The PriceComponent.
 */
export const PriceComponent: React.FunctionComponent<IPriceComponentProps> = msdyn365Commerce.createComponentOverride<IPriceComponent>('Price', {
    component: Price,
    ...priceComponentActions
});


export default PriceComponent;